/**
 * The last step of the two write-y recovery procedures.
 *
 * "Upload the regrant file it produces" and "Upload the result" were the final
 * lines of the restore and rotate procedures, and there was nowhere to upload
 * anything — so both stopped one step from the end, after all the difficult
 * parts had already been done with the shares in hand.
 *
 * The file is parsed here rather than posted raw, for two reasons. Its `kind`
 * says which of the two operations it is, so uploading a rotation into the
 * restore slot can be refused by name instead of by a server-side type error.
 * And the reason field can then be asked for beside a summary of what is about
 * to happen — "give ana back Slovenia" rather than "confirm".
 *
 * A reason is required. A key that can open any group is worth having only if
 * every use of it leaves a record naming why, and a field that can be skipped
 * is a field that will be.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { errorMessage, trpc, trpcClient } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { Checkbox } from "../../components/ui/Checkbox.js";
import {
  ChallengeFields,
  challengeReady,
  type ChallengeState,
} from "./ChallengeFields.js";
import { Input } from "../../components/ui/Input.js";
import { useToast } from "../../components/ui/Toast.js";
import { openFilePicker } from "../../lib/filePicker.js";

interface Regrant {
  kind: "friends-cloud/regrant";
  groupId: string;
  username: string;
  wrappedGroupKey: string;
}

interface Rotation {
  version: number;
  groups: { groupId: string; wrappedGroupKey: string }[];
}

type Loaded =
  | { sort: "regrant"; file: Regrant }
  | { sort: "rotation"; file: Rotation };

export function UploadRecoveryFile({ expects }: { expects: "regrant" | "rotation" }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [reason, setReason] = useState("");
  /*
   * Off until asked for. The procedure this serves begins with "ask the person
   * to create a new account", which is by definition not a member — so this is
   * the ordinary case, not the exception, and it still has to be said out loud.
   */
  const [admit, setAdmit] = useState(false);

  /*
   * The challenge, and its answer.
   *
   * Nothing here can check that the key inside an uploaded file is the right
   * one — checking would mean being able to open it. So the server checks
   * who produced the file instead: it seals a random number to the recovery
   * public key, and only the private half, rebuilt from the shares, can hand
   * it back. Without this step a file of the right shape was accepted from
   * anybody.
   */
  const [challenge, setChallenge] = useState<ChallengeState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const apply = useMutation({
    mutationFn: async () => {
      if (!loaded) return;
      if (loaded.sort === "regrant") {
        await trpcClient.admin.applyRegrant.mutate({
          groupId: loaded.file.groupId,
          username: loaded.file.username,
          wrappedGroupKey: loaded.file.wrappedGroupKey,
          admit,
          reason: reason.trim(),
          challengeToken: challenge?.token ?? "",
          challengeAnswer: challenge?.answer.trim() ?? "",
        });
      } else {
        await trpcClient.admin.applyRotation.mutate({
          groups: loaded.file.groups,
          reason: reason.trim(),
          challengeToken: challenge?.token ?? "",
          challengeAnswer: challenge?.answer.trim() ?? "",
        });
      }
    },
    onSuccess: async () => {
      toast(
        loaded?.sort === "regrant"
          ? `${loaded.file.username} has their group back.`
          : "Rotation applied. The new key is active — you can destroy the old shares.",
      );
      setLoaded(null);
      setReason("");
      setAdmit(false);
      setChallenge(null);
      setError(null);
      await queryClient.invalidateQueries({
        queryKey: trpc.admin.securityStatus.queryKey(),
      });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  async function read(file: File): Promise<void> {
    setError(null);

    try {
      const parsed: unknown = JSON.parse(await file.text());

      if (
        expects === "regrant" &&
        isRegrant(parsed)
      ) {
        setLoaded({ sort: "regrant", file: parsed });
        return;
      }

      if (expects === "rotation" && isRotation(parsed)) {
        setLoaded({ sort: "rotation", file: parsed });
        return;
      }

      /*
       * Named, not generic. The two files come out of the same tool minutes
       * apart and are trivially swapped, and "invalid file" would send somebody
       * back to re-run a command that was fine.
       */
      setError(
        expects === "regrant"
          ? "That is not a regrant file. It should be the one open-group wrote."
          : "That is not a rotation file. It should be the one rotate wrote.",
      );
    } catch {
      setError("That file is not readable JSON.");
    }
  }

  return (
    <div className="fc-security__export">
      {!loaded ? (
        <Button
          variant="secondary"
          onClick={() =>
            openFilePicker({
              accept: "application/json,.json",
              onPick: (files) => {
                if (files[0]) void read(files[0]);
              },
            })
          }
        >
          {expects === "regrant" ? "Upload regrant file" : "Upload rotation file"}
        </Button>
      ) : (
        <div className="fc-security__upload">
          <p className="fc-admin__hint">
            {loaded.sort === "regrant"
              ? `Give ${loaded.file.username} back access to ${loaded.file.groupId}.`
              : `Re-point ${String(loaded.file.groups.length)} groups at the new recovery key.`}
          </p>

          {loaded.sort === "regrant" && (
            <Checkbox
              checked={admit}
              label={`Also add ${loaded.file.username} to the group`}
              onChange={(event) => setAdmit(event.target.checked)}
            />
          )}

          <ChallengeFields id={expects} state={challenge} onChange={setChallenge} />

          <Field label="Why" htmlFor={`fc-reason-${expects}`} required>
            <Input
              id={`fc-reason-${expects}`}
              value={reason}
              maxLength={500}
              placeholder={
                loaded.sort === "regrant"
                  ? "Support request #12 — every member lost their password"
                  : "Yearly rotation"
              }
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>

          <div className="fc-security__export">
            <Button
              variant="primary"
              loading={apply.isPending}
              disabled={reason.trim().length < 3 || !challengeReady(challenge)}
              onClick={() => apply.mutate()}
            >
              Apply
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setLoaded(null);
                setError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {error && (
        <p className="fc-admin__error" role="alert">
          {error}
        </p>
      )}

    </div>
  );
}

function isRegrant(value: unknown): value is Regrant {
  const file = value as Partial<Regrant> | null;
  return (
    file?.kind === "friends-cloud/regrant" &&
    typeof file.groupId === "string" &&
    typeof file.username === "string" &&
    typeof file.wrappedGroupKey === "string"
  );
}

function isRotation(value: unknown): value is Rotation {
  const file = value as Partial<Rotation> | null;
  return (
    Array.isArray(file?.groups) &&
    file.groups.length > 0 &&
    file.groups.every(
      (group) =>
        typeof group?.groupId === "string" &&
        typeof group?.wrappedGroupKey === "string",
    )
  );
}
